import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useConfirm } from '../context/ConfirmContext';
import { getBackendUrl } from '../config';

const ERROR_AUTO_DISMISS_MS = 8003;

const BACKEND_URL = getBackendUrl();

// Available navigation routes
const NAVIGATION_ROUTES = [
  { path: '/dashboard', label: 'Dashboard' },
  { path: '/devices', label: 'Pajisjet' },
  { path: '/kosova', label: 'Kosova' },
  { path: '/alarms', label: 'Alarmet' },
  { path: '/reports', label: 'Raportet' },
  { path: '/analysis', label: 'Analysis' },
  { path: '/production-planning', label: 'Planifikimi' },
  { path: '/control', label: 'Kontroll' },
  { path: '/users', label: 'Përdoruesit' },
  { path: '/companies', label: 'Kompanitë' },
  { path: '/settings', label: 'Cilësimet' },
  { path: '/about', label: 'Rreth' },
];

const Users = () => {
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const { confirm } = useConfirm();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const errorDismissTimerRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [showCompanyPopup, setShowCompanyPopup] = useState(false);
  const [showRoutesPopup, setShowRoutesPopup] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    username: '',
    email: '',
    role: 'VIEWER',
    company_ids: [],
    allowed_routes: [],
    password: '',
    status: 'ACTIVE'
  });

  // Check if mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
    return () => { if (errorDismissTimerRef.current) clearTimeout(errorDismissTimerRef.current); };
  }, []);

  const dismissUsersError = () => {
    if (errorDismissTimerRef.current) { clearTimeout(errorDismissTimerRef.current); errorDismissTimerRef.current = null; }
    setUsersError(null);
  };

  const fetchUsers = async () => {
    if (errorDismissTimerRef.current) { clearTimeout(errorDismissTimerRef.current); errorDismissTimerRef.current = null; }
    setUsersError(null);
    const url = `${BACKEND_URL}/api/users`;
    try {
      const response = await axios.get(url);
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      const isNetworkError = !error.response || (error.message && error.message.toLowerCase().includes('network'));
      const msg = isNetworkError ? 'Lidhja me serverin dështoi.' : (error.response?.data?.detail ?? error.message);
      if (errorDismissTimerRef.current) clearTimeout(errorDismissTimerRef.current);
      setUsersError(msg || 'Gabim në ngarkimin e listës.');
      errorDismissTimerRef.current = setTimeout(() => { setUsersError(null); errorDismissTimerRef.current = null; }, ERROR_AUTO_DISMISS_MS);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      first_name: '',
      username: '',
      email: '',
      role: 'VIEWER',
      company_ids: companies.length > 0 ? [companies[0].id] : [],
      allowed_routes: NAVIGATION_ROUTES.map(r => r.path), // All routes by default
      password: '',
      status: 'ACTIVE'
    });
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    const fullName = user.last_name ? `${user.first_name} ${user.last_name}` : user.first_name;
    setFormData({
      first_name: fullName,
      username: user.username,
      email: user.email,
      role: user.role,
      company_ids: user.company_ids || (user.company_id ? [user.company_id] : []),
      allowed_routes: user.allowed_routes || NAVIGATION_ROUTES.map(r => r.path),
      password: '',
      status: user.status
    });
    setShowModal(true);
    setOpenDropdown(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const nameParts = formData.first_name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const submitData = {
        ...formData,
        first_name: firstName,
        last_name: lastName
      };

      // IMPORTANT: On edit, if password is empty, do NOT send it (otherwise it overwrites password with empty)
      if (editingUser && (!submitData.password || submitData.password.trim() === '')) {
        delete submitData.password;
      }

      if (editingUser) {
        await axios.put(`${BACKEND_URL}/api/users/${editingUser.id}`, submitData);
      } else {
        await axios.post(`${BACKEND_URL}/api/users`, submitData);
      }
      setShowModal(false);
      setShowCompanyPopup(false);
      setShowRoutesPopup(false);
      fetchUsers();
    } catch (error) {
      const isNetwork = !error.response || (error.message && String(error.message).toLowerCase().includes('network'));
      const msg = isNetwork ? 'Lidhja me serverin dështoi. Kontrolloni që backend-i të jetë i ndezur.' : (error.response?.data?.detail ?? error.message ?? 'Gabim gjatë ruajtjes.');
      const detail = Array.isArray(msg) ? msg.map((e) => e.msg || e).join(', ') : msg;
      toast.error(detail);
    }
  };

  const handleDelete = async (userId) => {
    const ok = await confirm({ title: 'Konfirmo fshirjen', message: 'A jeni të sigurt që dëshironi të fshini këtë përdorues?', confirmLabel: 'Fshi', cancelLabel: 'Jo' });
    if (!ok) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/users/${userId}`);
      fetchUsers();
      setOpenDropdown(null);
    } catch (error) {
      const isNetwork = !error.response || (error.message && String(error.message).toLowerCase().includes('network'));
      toast.error(isNetwork ? 'Lidhja me serverin dështoi. Kontrolloni që backend-i të jetë i ndezur.' : (error.response?.data?.detail || 'Gabim gjatë fshirjes.'));
    }
  };

  const getSelectedCompanyNames = () => {
    if (formData.company_ids.length === 0) return t('select');
    if (formData.company_ids.length === 1) {
      const company = companies.find(c => c.id === formData.company_ids[0]);
      return company?.name || t('select');
    }
    return `${formData.company_ids.length} ${t('companiesCount')}`;
  };

  const getSelectedRoutesLabel = () => {
    if (formData.allowed_routes.length === 0) return t('none');
    if (formData.allowed_routes.length === NAVIGATION_ROUTES.length) return t('all');
    return `${formData.allowed_routes.length} ${t('pages')}`;
  };

  // Kufizime pamje përdorues: Admin vetëm VIEWER mund t’i ndryshojë/fshijë; Superadmin bën gjithçka.
  const canEditUser = (user) => {
    if (!currentUser) return false;
    if (currentUser.role === 'SUPERADMIN') return true;
    if (currentUser.role === 'ADMIN') return user?.role === 'VIEWER';
    return false;
  };
  const canDeleteUser = (user) => {
    if (!currentUser || user?.id === currentUser.id) return false;
    if (currentUser.role === 'SUPERADMIN') return true;
    if (currentUser.role === 'ADMIN') return user?.role === 'VIEWER';
    return false;
  };
  const canCreateOnlyViewer = currentUser?.role === 'ADMIN';

  return (
    <Layout>
      <div className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 md:mb-6 gap-3">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{t('usersTitle')}</h2>
          <button onClick={handleCreate} data-testid="add-user-btn"
            className="w-full md:w-auto px-4 py-2.5 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm md:text-base">
            {t('addUser')}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-sm md:text-base">{t('loading')}</div>
        ) : usersError ? (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 text-amber-800 dark:text-amber-200 relative" role="alert">
            <div className="flex items-start justify-between gap-3 pr-1">
              <div className="flex-1 min-w-0">
                <p className="font-medium mb-1">{usersError}</p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">{t('ensureBackendRunning')}</p>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => { dismissUsersError(); fetchUsers(); }} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium">
                    {t('tryAgain')}
                  </button>
                  <button type="button" onClick={dismissUsersError} className="px-4 py-2 bg-transparent border border-amber-600 dark:border-amber-500 text-amber-800 dark:text-amber-200 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-800/50 text-sm font-medium">
                    {t('closeMessage')}
                  </button>
                </div>
              </div>
              <button type="button" onClick={dismissUsersError} className="shrink-0 p-2 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-800/50 text-amber-800 dark:text-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500" aria-label="Mbylle mesazhin">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {(Array.isArray(users) ? users : []).map((user) => (
                <div key={user.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">{user.first_name} {user.last_name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">@{user.username}</p>
                    </div>
                    <div className="relative ml-2">
                      <button onClick={() => setOpenDropdown(openDropdown === user.id ? null : user.id)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 12c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" /></svg>
                      </button>
                      {openDropdown === user.id && (
                        <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-700 rounded-lg shadow-lg z-10 border border-gray-200 dark:border-gray-600">
                          {canEditUser(user) && <button onClick={() => handleEdit(user)} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-t-lg">Edit</button>}
                          {canDeleteUser(user) && <button onClick={() => handleDelete(user.id)} className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-b-lg">Fshi</button>}
                          {!canEditUser(user) && !canDeleteUser(user) && user.id !== currentUser?.id && <span className="block px-4 py-2 text-xs text-gray-500 dark:text-gray-400">Nuk lejohet</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 dark:text-gray-400">Email:</span>
                      <span className="text-gray-900 dark:text-white truncate">{user.email}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                      <span className={`px-2 py-1 text-xs rounded-full ${user.role === 'SUPERADMIN' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' : user.role === 'ADMIN' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>{user.role}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${user.status === 'ACTIVE' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>{user.status}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase">{t('name')}</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase">Username</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase">{t('company')}</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase">{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {(Array.isArray(users) ? users : []).map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{user.first_name} {user.last_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{user.username}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{user.email}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{Array.isArray(user.company_ids) && user.company_ids.length > 0
                          ? user.company_ids.map(id => companies.find(c => c.id === id)?.name).filter(Boolean).join(', ')
                          : '-'}</td>
                        <td className="px-6 py-4"><span className={`px-2.5 py-1 text-xs font-medium rounded-full ${user.status === 'ACTIVE' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>{user.status}</span></td>
                        <td className="px-6 py-4 text-sm">
                          {canEditUser(user) && <button onClick={() => handleEdit(user)} className="text-blue-600 dark:text-blue-400 hover:underline mr-3">Edit</button>}
                          {canDeleteUser(user) && <button onClick={() => handleDelete(user.id)} className="text-red-600 dark:text-red-400 hover:underline">Fshi</button>}
                          {!canEditUser(user) && !canDeleteUser(user) && user.id !== currentUser?.id && <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Main Modal - mobile: në mes, kolona të ngushta, X; desktop: si më parë */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-sm md:max-w-xl shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
                <h3 className="text-base font-bold text-gray-900 dark:text-white">{editingUser ? 'Ndrysho Përdoruesin' : 'Shto Përdorues të Ri'}</h3>
                <button type="button" onClick={() => { setShowModal(false); setShowCompanyPopup(false); setShowRoutesPopup(false); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600" aria-label="Mbyll">✕</button>
              </div>

              <form onSubmit={handleSubmit} className="px-2 md:px-3 py-2">
                {isMobile ? (
                  /* MOBILE: Single column, compact - një fushe për rresht */
                  <div className="space-y-1">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Emër Mbiemër</label>
                      <input type="text" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder="Emri Mbiemri" style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Username</label>
                      <input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder="username" style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required disabled={!!editingUser} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">{editingUser ? 'Fjalëkalimi (opsional)' : 'Fjalëkalimi'}</label>
                      <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="••••••••" style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required={!editingUser} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Email</label>
                      <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@example.com" style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Role</label>
                      <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required>
                        {!canCreateOnlyViewer && currentUser.role === 'SUPERADMIN' && <option value="SUPERADMIN">SUPERADMIN</option>}
                        {!canCreateOnlyViewer && <option value="ADMIN">ADMIN</option>}
                        <option value="VIEWER">VIEWER</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Status</label>
                      <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="INACTIVE">INACTIVE</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Kompania</label>
                      <button type="button" onClick={() => setShowCompanyPopup(true)} style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-left flex items-center justify-between">
                        <span className="truncate">{getSelectedCompanyNames()}</span>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </button>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Navigimi</label>
                      <button type="button" onClick={() => setShowRoutesPopup(true)} style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-left flex items-center justify-between">
                        <span className="truncate">{getSelectedRoutesLabel()}</span>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  /* WEB: Two columns */
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Emri</label>
                        <input type="text" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder="Emri Mbiemri" style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Username</label>
                        <input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder="username" style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required disabled={!!editingUser} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">{editingUser ? 'Fjalëkalimi (opsional)' : 'Fjalëkalimi'}</label>
                        <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="••••••••" style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required={!editingUser} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Email</label>
                        <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@example.com" style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Role</label>
                        <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required>
                          {!canCreateOnlyViewer && currentUser.role === 'SUPERADMIN' && <option value="SUPERADMIN">SUPERADMIN</option>}
                          {!canCreateOnlyViewer && <option value="ADMIN">ADMIN</option>}
                          <option value="VIEWER">VIEWER</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Status</label>
                        <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                          <option value="ACTIVE">ACTIVE</option>
                          <option value="INACTIVE">INACTIVE</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Kompania</label>
                        <button type="button" onClick={() => setShowCompanyPopup(true)} style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-600">
                          <span className="truncate">{getSelectedCompanyNames()}</span>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </button>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Navigimi</label>
                        <button type="button" onClick={() => setShowRoutesPopup(true)} style={{ height: '32px' }} className="w-full px-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-600">
                          <span className="truncate">{getSelectedRoutesLabel()}</span>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 mt-3">
                  <button type="button" onClick={() => { setShowModal(false); setShowCompanyPopup(false); setShowRoutesPopup(false); }} style={{ height: '32px' }} className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition">Anulo</button>
                  <button type="submit" style={{ height: '32px' }} className="flex-1 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 transition">Ruaj</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Company Selection Popup - mobile: në mes, X */}
        {showCompanyPopup && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-sm shadow-2xl">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-base font-bold text-gray-900 dark:text-white">Zgjidh Kompanin</h4>
                <button type="button" onClick={() => setShowCompanyPopup(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400" aria-label="Mbyll">✕</button>
              </div>
              <div className="p-2 max-h-64 overflow-y-auto">
                {companies.map(company => (
                  <label key={company.id} className="flex items-center px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer rounded-lg transition">
                    <input type="checkbox" checked={formData.company_ids.includes(company.id)}
                      onChange={(e) => {
                        if (e.target.checked) setFormData({ ...formData, company_ids: [...formData.company_ids, company.id] });
                        else setFormData({ ...formData, company_ids: formData.company_ids.filter(id => id !== company.id) });
                      }}
                      className="w-4 h-4 mr-3 rounded border-gray-300 dark:border-gray-500 text-blue-600 focus:ring-blue-500" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{company.name}</span>
                  </label>
                ))}
              </div>
              <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex gap-3">
                <button type="button" onClick={() => setShowCompanyPopup(false)} style={{ height: '32px' }} className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded text-sm font-medium">Anulo</button>
                <button type="button" onClick={() => setShowCompanyPopup(false)} style={{ height: '32px' }} className="flex-1 bg-blue-600 text-white rounded text-sm font-medium">Ruaj</button>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Routes Selection Popup - mobile: në mes, X */}
        {showRoutesPopup && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-sm shadow-2xl">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-base font-bold text-gray-900 dark:text-white">Navigimi i Lejuar</h4>
                <button type="button" onClick={() => setShowRoutesPopup(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400" aria-label="Mbyll">✕</button>
              </div>
              <div className="p-2 max-h-64 overflow-y-auto">
                {NAVIGATION_ROUTES.map(route => (
                  <label key={route.path} className="flex items-center px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer rounded-lg transition">
                    <input type="checkbox" checked={formData.allowed_routes.includes(route.path)}
                      onChange={(e) => {
                        if (e.target.checked) setFormData({ ...formData, allowed_routes: [...formData.allowed_routes, route.path] });
                        else setFormData({ ...formData, allowed_routes: formData.allowed_routes.filter(p => p !== route.path) });
                      }}
                      className="w-4 h-4 mr-3 rounded border-gray-300 dark:border-gray-500 text-blue-600 focus:ring-blue-500" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{route.label}</span>
                  </label>
                ))}
              </div>
              <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex gap-3">
                <button type="button" onClick={() => setShowRoutesPopup(false)} style={{ height: '32px' }} className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded text-sm font-medium">Anulo</button>
                <button type="button" onClick={() => setShowRoutesPopup(false)} style={{ height: '32px' }} className="flex-1 bg-blue-600 text-white rounded text-sm font-medium">Ruaj</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Users;
